// client/src/pages/AttorneyIntake.tsx - REDESIGNED WITH PROFESSIONAL TYPE TOGGLE
import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import StateSelect from '../components/StateSelect';
import { useNavigate } from 'react-router-dom';

const PRACTICE_AREAS = [
    'Medical Malpractice', 'Personal Injury', 'Product Liability', 'Criminal Defense',
    'Civil Litigation', 'Workers Compensation', 'Insurance Defense', 'Employment Law',
    'Corporate Law', 'Family Law', 'Real Estate Law', 'Intellectual Property',
    'Environmental Law', 'Tax Law', 'Immigration Law', 'Other'
];

const CASE_TYPES = [
    'Medical Malpractice', 'Motor Vehicle Accidents', 'Premises Liability', 'Product Defects',
    'Professional Negligence', 'Wrongful Death', 'Birth Injuries', 'Surgical Errors',
    'Nursing Home Negligence', 'Pharmaceutical Liability', 'Medical Device Liability',
    'Hospital Negligence', 'Diagnostic Errors', 'Other'
];

const EXPERT_TYPES = [
    'Physicians', 'Nurses', 'Pharmacists', 'Physical Therapists',
    'Mental Health Professionals', 'Engineers', 'Economists',
    'Vocational Experts', 'Life Care Planners', 'Accident Reconstructionists', 'Other'
];

const FIRM_SIZES = ['Solo Practice', '2-10 attorneys', '11-50 attorneys', '51-200 attorneys', '200+ attorneys'];

type UserCategory = 'attorney' | 'not_attorney';
type NonAttorneyType = 'paralegal' | 'medical_consultant' | 'nursing_consultant';

interface License {
    id: string;
    type: 'bar' | 'npi' | 'nursing';
    number: string;
    state: string[];
}

export default function AttorneyIntake() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [hasProfile, setHasProfile] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [user, setUser] = useState<any>(null);

    // Core fields
    const [name, setName] = useState('');
    const [firmName, setFirmName] = useState('');
    const [phone, setPhone] = useState('');
    const [stateOfPractice, setStateOfPractice] = useState<string[]>([]);
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [yearsExperience, setYearsExperience] = useState<string>('');
    const [firmSize, setFirmSize] = useState('');

    // Professional type
    const [userCategory, setUserCategory] = useState<UserCategory>('attorney');
    const [nonAttorneyType, setNonAttorneyType] = useState<NonAttorneyType | ''>('');

    // Licenses (multiple)
    const [licenses, setLicenses] = useState<License[]>([{ id: '1', type: 'bar', number: '', state: [] }]);

    // Paralegal fields
    const [supervisingAttorneyName, setSupervisingAttorneyName] = useState('');
    const [supervisingAttorneyBar, setSupervisingAttorneyBar] = useState('');
    const [supervisingAttorneyState, setSupervisingAttorneyState] = useState<string[]>([]);

    // Medical/Nursing consultant
    const [npi, setNpi] = useState('');

    // Practice details
    const [practiceAreas, setPracticeAreas] = useState<string[]>([]);
    const [casesHandled, setCasesHandled] = useState<string[]>([]);
    const [preferredExpertTypes, setPreferredExpertTypes] = useState<string[]>([]);
    const [jurisdictions, setJurisdictions] = useState<string[]>([]);
    const [caseVolume, setCaseVolume] = useState('');
    const [budgetRange, setBudgetRange] = useState('');
    const [searchKeywords, setSearchKeywords] = useState('');
    const [referralSources, setReferralSources] = useState('');

    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        checkExistingProfile();
    }, []);

    const checkExistingProfile = async () => {
        try {
            const { data: { user: authUser }, error } = await supabase.auth.getUser();
            if (error || !authUser) { navigate('/login'); return; }
            setUser(authUser);
            setUserEmail(authUser.email || '');

            const { data: attorneyData } = await supabase
                .from('attorneys')
                .select('id')
                .eq('user_id', authUser.id)
                .maybeSingle();

            if (attorneyData) {
                setHasProfile(true);
                navigate('/attorney-dashboard');
                return;
            }

            const userType = authUser.user_metadata?.user_type;
            if (userType !== 'attorney') { navigate('/'); return; }
            setLoading(false);
        } catch {
            setLoading(false);
        }
    };

    // License management
    const addLicense = () => {
        const defaultType = userCategory === 'attorney' ? 'bar' :
            nonAttorneyType === 'medical_consultant' ? 'npi' : 'nursing';
        setLicenses([...licenses, { id: Date.now().toString(), type: defaultType, number: '', state: [] }]);
    };

    const removeLicense = (id: string) => {
        if (licenses.length > 1) setLicenses(licenses.filter(l => l.id !== id));
    };

    const updateLicense = (id: string, field: keyof License, value: any) => {
        setLicenses(licenses.map(l => l.id === id ? { ...l, [field]: value } : l));
    };

    // Checkbox helpers
    const toggleArray = (arr: string[], item: string, setter: (v: string[]) => void) => {
        setter(arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item]);
    };

    // Get professional type string for DB
    const getProfessionalType = () => {
        if (userCategory === 'attorney') return 'Attorney';
        if (nonAttorneyType === 'paralegal') return 'Paralegal';
        if (nonAttorneyType === 'medical_consultant') return 'Medical Consultant';
        if (nonAttorneyType === 'nursing_consultant') return 'Legal Nurse Consultant';
        return 'Other Legal Professional';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setMessage('');

        try {
            if (!user) { setMessage('Error: No user found.'); setSubmitting(false); return; }

            const attorneyData = {
                user_id: user.id,
                name,
                professional_type: getProfessionalType(),
                firm_name: firmName,
                license_number: licenses[0]?.number || null,
                bar_number: userCategory === 'attorney' ? licenses[0]?.number || null : null,
                phone_number: phone,
                email: user.email,
                website_url: websiteUrl ? (websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`) : null,
                state_of_practice: stateOfPractice[0] || '',
                jurisdictions,
                practice_areas: practiceAreas,
                search_keywords: searchKeywords,
                cases_handled: casesHandled,
                preferred_expert_types: preferredExpertTypes,
                years_experience: Number(yearsExperience) || 0,
                firm_size: firmSize,
                case_volume: caseVolume,
                budget_range: budgetRange,
                referral_sources: referralSources,
                npi: npi || null,
                supervising_attorney_name: supervisingAttorneyName || null,
                supervising_attorney_bar: supervisingAttorneyBar || null,
                supervising_attorney_state: supervisingAttorneyState[0] || null,
                licenses: JSON.stringify(licenses),
                profile_picture_url: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=4B5563&color=fff&size=200`,
                is_verified: false,
                email_verified: user.email_confirmed_at ? true : false,
                tier: 3,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            const { error: insertError } = await supabase.from('attorneys').insert([attorneyData]).select().maybeSingle();
            if (insertError) throw insertError;

            // Notify admin of new user
            try {
                const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
                await fetch(`${apiUrl}/notify/new-user`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_name: name,
                        user_email: user.email,
                        user_type: 'attorney'
                    })
                });
            } catch (e) {}

            setMessage('✅ Profile submitted successfully! Redirecting to your dashboard...');
            setTimeout(() => { window.location.href = '/attorney-dashboard'; }, 2000);
        } catch (error: any) {
            setMessage(`Error: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading intake form...</p>
                </div>
            </div>
        );
    }

    if (hasProfile) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600">You already have a profile. Redirecting...</p>
                </div>
            </div>
        );
    }

    const inputClass = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-6">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white">Legal Professional Registration</h2>
                        <p className="text-blue-100 mt-2">Complete your professional profile to access expert witness services</p>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 m-6">
                        <p className="text-sm text-blue-700">
                            <strong>Professional Verification:</strong> After submitting, our team will review and verify your credentials within 2-3 business days.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-8">

                        {/* I am a... */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">I am a...</h3>
                            <div className="flex gap-4 mb-6">
                                <button
                                    type="button"
                                    onClick={() => { setUserCategory('attorney'); setNonAttorneyType(''); setLicenses([{ id: '1', type: 'bar', number: '', state: [] }]); }}
                                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition border-2 ${userCategory === 'attorney' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                                >
                                    Attorney
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setUserCategory('not_attorney'); setLicenses([{ id: '1', type: 'npi', number: '', state: [] }]); }}
                                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition border-2 ${userCategory === 'not_attorney' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                                >
                                    I am not an Attorney
                                </button>
                            </div>

                            {/* Non-attorney sub-type */}
                            {userCategory === 'not_attorney' && (
                                <div className="space-y-4">
                                    <p className="text-sm font-medium text-gray-700">Select your role:</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {[
                                            { value: 'paralegal' as NonAttorneyType, label: 'Paralegal' },
                                            { value: 'medical_consultant' as NonAttorneyType, label: 'Medical Consultant' },
                                            { value: 'nursing_consultant' as NonAttorneyType, label: 'Legal Nurse Consultant' },
                                        ].map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => {
                                                    setNonAttorneyType(opt.value);
                                                    setLicenses([{ id: '1', type: opt.value === 'medical_consultant' ? 'npi' : opt.value === 'nursing_consultant' ? 'nursing' : 'bar', number: '', state: [] }]);
                                                }}
                                                className={`py-2 px-3 rounded-lg text-sm font-medium transition border-2 ${nonAttorneyType === opt.value ? 'bg-blue-100 text-blue-800 border-blue-400' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Paralegal notice */}
                                    {nonAttorneyType === 'paralegal' && (
                                        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                                            <p className="text-sm text-amber-800">
                                                <strong>Note:</strong> Paralegals must operate under a supervising attorney. Please include the supervising attorney's information below.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Personal Information */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Personal Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                                    <input type="text" required value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="John Smith, Esq." />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Firm/Organization <span className="text-red-500">*</span></label>
                                    <input type="text" required value={firmName} onChange={e => setFirmName(e.target.value)} className={inputClass} placeholder="Smith & Associates" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience <span className="text-red-500">*</span></label>
                                    <input type="number" required min="0" value={yearsExperience} onChange={e => setYearsExperience(e.target.value)} className={inputClass} placeholder="10" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Firm Size <span className="text-red-500">*</span></label>
                                    <select required value={firmSize} onChange={e => setFirmSize(e.target.value)} className={inputClass}>
                                        <option value="">Select firm size</option>
                                        {FIRM_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary State <span className="text-red-500">*</span></label>
                                    <StateSelect value={stateOfPractice} onChange={setStateOfPractice} multiple={false} required={true} className={inputClass} />
                                </div>
                            </div>
                        </div>

                        {/* Licensing Section — Attorney */}
                        {userCategory === 'attorney' && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Bar Licenses</h3>
                                {licenses.map((lic, idx) => (
                                    <div key={lic.id} className="mb-4 p-4 bg-gray-50 rounded-lg border">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-sm font-medium text-gray-700">License {idx + 1}</span>
                                            {licenses.length > 1 && (
                                                <button type="button" onClick={() => removeLicense(lic.id)} className="text-red-500 text-sm hover:text-red-700">Remove</button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Bar Number <span className="text-red-500">*</span></label>
                                                <input type="text" required value={lic.number} onChange={e => updateLicense(lic.id, 'number', e.target.value)} className={inputClass} placeholder="Bar Number" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">State <span className="text-red-500">*</span></label>
                                                <StateSelect value={lic.state} onChange={v => updateLicense(lic.id, 'state', v)} multiple={false} required={true} className={inputClass} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={addLicense} className="text-blue-600 text-sm font-medium hover:text-blue-800 flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    Add Another License
                                </button>
                            </div>
                        )}

                        {/* Paralegal — Supervising Attorney */}
                        {userCategory === 'not_attorney' && nonAttorneyType === 'paralegal' && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Supervising Attorney Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Supervising Attorney Name <span className="text-red-500">*</span></label>
                                        <input type="text" required value={supervisingAttorneyName} onChange={e => setSupervisingAttorneyName(e.target.value)} className={inputClass} placeholder="Jane Doe, Esq." />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Supervising Attorney Bar Number <span className="text-red-500">*</span></label>
                                        <input type="text" required value={supervisingAttorneyBar} onChange={e => setSupervisingAttorneyBar(e.target.value)} className={inputClass} placeholder="Bar Number" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                                        <StateSelect value={supervisingAttorneyState} onChange={setSupervisingAttorneyState} multiple={false} required={true} className={inputClass} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Medical Consultant — NPI */}
                        {userCategory === 'not_attorney' && nonAttorneyType === 'medical_consultant' && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Medical Licenses</h3>
                                {licenses.map((lic, idx) => (
                                    <div key={lic.id} className="mb-4 p-4 bg-gray-50 rounded-lg border">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-sm font-medium text-gray-700">License {idx + 1}</span>
                                            {licenses.length > 1 && (
                                                <button type="button" onClick={() => removeLicense(lic.id)} className="text-red-500 text-sm hover:text-red-700">Remove</button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">NPI Number <span className="text-red-500">*</span></label>
                                                <input type="text" required value={lic.number} onChange={e => updateLicense(lic.id, 'number', e.target.value)} className={inputClass} placeholder="10-digit NPI (may include RN/MD prefix)" />
                                                <p className="text-xs text-gray-400 mt-1">Typically 10 digits. Some states include RN or MD prefix.</p>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">State <span className="text-red-500">*</span></label>
                                                <StateSelect value={lic.state} onChange={v => updateLicense(lic.id, 'state', v)} multiple={false} required={true} className={inputClass} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={addLicense} className="text-blue-600 text-sm font-medium hover:text-blue-800 flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    Add Another License
                                </button>
                            </div>
                        )}

                        {/* Nursing Consultant — Nursing License */}
                        {userCategory === 'not_attorney' && nonAttorneyType === 'nursing_consultant' && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Nursing Licenses</h3>
                                {licenses.map((lic, idx) => (
                                    <div key={lic.id} className="mb-4 p-4 bg-gray-50 rounded-lg border">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-sm font-medium text-gray-700">License {idx + 1}</span>
                                            {licenses.length > 1 && (
                                                <button type="button" onClick={() => removeLicense(lic.id)} className="text-red-500 text-sm hover:text-red-700">Remove</button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Nursing License Number <span className="text-red-500">*</span></label>
                                                <input type="text" required value={lic.number} onChange={e => updateLicense(lic.id, 'number', e.target.value)} className={inputClass} placeholder="Nursing License Number" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">State <span className="text-red-500">*</span></label>
                                                <StateSelect value={lic.state} onChange={v => updateLicense(lic.id, 'state', v)} multiple={false} required={true} className={inputClass} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={addLicense} className="text-blue-600 text-sm font-medium hover:text-blue-800 flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    Add Another License
                                </button>
                            </div>
                        )}

                        {/* Licensed Jurisdictions */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Licensed Jurisdictions</h3>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select all states where you are licensed</label>
                                <StateSelect value={jurisdictions} onChange={setJurisdictions} multiple={true} className={inputClass} />
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Contact Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email (from your account)</label>
                                    <input type="email" disabled value={userEmail} className={`${inputClass} bg-gray-100 text-gray-600`} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                                    <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} placeholder="(555) 123-4567" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Firm Website</label>
                                    <div className="flex">
                                        <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">https://</span>
                                        <input
                                            type="text"
                                            value={websiteUrl.replace(/^https?:\/\//, '')}
                                            onChange={e => setWebsiteUrl(e.target.value.replace(/^https?:\/\//, ''))}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="yourfirm.com"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Practice Areas */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Practice Areas</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {PRACTICE_AREAS.map(area => (
                                    <label key={area} className="flex items-center cursor-pointer">
                                        <input type="checkbox" checked={practiceAreas.includes(area)} onChange={() => toggleArray(practiceAreas, area, setPracticeAreas)} className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                        <span className="text-sm text-gray-700">{area}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Case Types */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Case Types Handled</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {CASE_TYPES.map(ct => (
                                    <label key={ct} className="flex items-center cursor-pointer">
                                        <input type="checkbox" checked={casesHandled.includes(ct)} onChange={() => toggleArray(casesHandled, ct, setCasesHandled)} className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                        <span className="text-sm text-gray-700">{ct}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Preferred Expert Types */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Preferred Expert Types</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {EXPERT_TYPES.map(et => (
                                    <label key={et} className="flex items-center cursor-pointer">
                                        <input type="checkbox" checked={preferredExpertTypes.includes(et)} onChange={() => toggleArray(preferredExpertTypes, et, setPreferredExpertTypes)} className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                        <span className="text-sm text-gray-700">{et}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Practice Details */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b">Practice Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Annual Case Volume</label>
                                    <select value={caseVolume} onChange={e => setCaseVolume(e.target.value)} className={inputClass}>
                                        <option value="">Select case volume</option>
                                        <option value="1-10">1-10 cases</option>
                                        <option value="11-25">11-25 cases</option>
                                        <option value="26-50">26-50 cases</option>
                                        <option value="51-100">51-100 cases</option>
                                        <option value="100+">100+ cases</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Typical Expert Budget Range</label>
                                    <select value={budgetRange} onChange={e => setBudgetRange(e.target.value)} className={inputClass}>
                                        <option value="">Select budget range</option>
                                        <option value="Under $5,000">Under $5,000</option>
                                        <option value="$5,000 - $15,000">$5,000 - $15,000</option>
                                        <option value="$15,000 - $30,000">$15,000 - $30,000</option>
                                        <option value="$30,000 - $50,000">$30,000 - $50,000</option>
                                        <option value="Over $50,000">Over $50,000</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">How did you hear about Expert Link Pro?</label>
                                    <textarea value={referralSources} onChange={e => setReferralSources(e.target.value)} rows={2} className={inputClass} placeholder="Colleague referral, online search, legal conference, etc." />
                                </div>
                            </div>
                        </div>

                        {/* Terms */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <label className="flex items-start cursor-pointer">
                                <input type="checkbox" required className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                <span className="text-sm text-gray-700">
                                    I certify that all information provided is accurate and complete. I understand that Expert Link Pro will verify my credentials and that providing false information may result in removal from the platform. I agree to the Terms of Service and Privacy Policy.
                                </span>
                            </label>
                        </div>

                        {/* Submit */}
                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={submitting}
                                className={`w-full py-3 px-4 rounded-lg text-white font-medium transition duration-200 ${submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                            >
                                {submitting ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Submitting...
                                    </span>
                                ) : 'Submit Registration'}
                            </button>
                        </div>

                        {message && (
                            <div className={`mt-4 p-4 rounded-lg ${message.includes('✅') ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                                {message}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}
